import React, { createContext, useContext, useEffect, useReducer, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { cometGet, eventGet, platformGetSettings, exchangeKeyWithHeaders, HEADER_CLIENT_ACCESS_TOKEN } from "@player-api";
import { Channel, Socket } from '../../../cp/components/Comet';
import LoadingMessage from "../components/LoadingMessage";
import StreamStreamListener from "./cometListener/StreamStreamListener";
import EpisodeListener from "./cometListener/EpisodeListener";
import BroadcastSessionListener from "./cometListener/BroadcastSessionListener";
import EventListener from "./cometListener/EventListener";
import StreamListener from "./cometListener/StreamListener";
import PageListener from "./cometListener/PageListener";
import MemberListener from "./cometListener/MemberListener";
import { useLocaleContext } from "./LocaleContext";
import tokenStorage from "../utils/tokenStorage";
import { services } from "@offline/components/SignIn/SocialLogin";

export const EventContext = createContext(null);

export const useEventContext = () => useContext(EventContext);

export const EventContextProvider = ({ participationType, watchKey, children }) => {
    const { setEventKey: changeLocaleEventKey, setEventLocale, isLoadingLocale } = useLocaleContext();
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const [event, eventDispatch] = useReducer((state, action) => action(state), null);
    const setEvent = (event) => eventDispatch(() => event);
    const [pages, pagesDispatch] = useReducer((state, action) => action(state), null);
    const setPages = (pages) => pagesDispatch(() => pages);
    const [cometServers, setCometServers] = useState(null);
    const [eventKey, setEventKey] = useState(null);
    const [initData, setInitData] = useState(null);

    const getEventKey = async (watchKey) => {
        try {
            const { data, headers } = await exchangeKeyWithHeaders(watchKey);
            const { eventKey } = data;

            tokenStorage.setEventKey(eventKey);

            if (!tokenStorage.get()) {
                tokenStorage.set(headers[HEADER_CLIENT_ACCESS_TOKEN]);
            }

            setInitData(data);
            changeLocaleEventKey(eventKey);
            setEventKey(eventKey);
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        if (!watchKey) {
            return;
        }

        getEventKey(watchKey);
    }, [watchKey]);

    useEffect(() => {
        if (!!eventKey && ['online', 'offline'].includes(participationType)) {
            localStorage.setItem(`participationType_${eventKey}`, participationType);
            const newPath = pathname.replace(participationType, 'event') + search;
            navigate(newPath);
        }
    }, [eventKey]);

    const eventHasAuth = !!(event?.moduleAuth && (['guest', 'email', 'phone', ...services].find((auth) => event.moduleAuth[auth])));

    useEffect(() => {
        if (!eventKey) {
            setEvent(null);
            return;
        }

        eventGet(eventKey)
            .then((event) => {
                for (let i in event.stream) {
                    if (!!event.stream[i].color && !event.stream[i].color.includes('#')) {
                        event.stream[i].color = `#${event.stream[i].color}`;
                    }
                    if (event.stream[i].episode) {
                        event.stream[i].episode = event.stream[i].episode.sort((a, b) => a.timeStart - b.timeStart);
                    }
                }

                setEvent(event);
                setEventLocale(event.locale);
            })
            .catch((e) => {
                console.error(e);
                setEvent(false);
            })
            ;

        platformGetSettings(eventKey)
            .then((pages) => setPages(pages))
            .catch((e) => {
                console.error(e);
                setPages([]);
            });

        cometGet(eventKey)
            .then((servers) => {
                setCometServers(servers);
            })
            .catch((e) => console.error(e));
    }, [eventKey]);

    if (null === event || !pages?.length || null === cometServers || isLoadingLocale || null === eventKey) {
        return <LoadingMessage isFullscreen isPreloader></LoadingMessage>
    }
    if (false === event) {
        return <LoadingMessage>Мероприятие не найдено</LoadingMessage>
    }

    const context = {
        eventKey,
        watchKey,
        eventHasAuth,
        event,
        setEvent,
        eventDispatch,
        pages,
        pagesDispatch,
        initData,
    };

    return (
        <EventContext.Provider value={context}>
            <Socket servers={cometServers}>
                <Channel name={eventKey} />

                <EventListener />
                <StreamListener />
                <StreamStreamListener />
                <BroadcastSessionListener />
                <EpisodeListener />
                <MemberListener />
                <PageListener />
                {children}
            </Socket>
        </EventContext.Provider>
    );
}
